import React, { useState, useEffect } from 'react';

export default function TimePicker({ onTimeChange, initialTimestamp = null, onClose = null }) {
  // Initialize with current time or provided timestamp
  const [timestamp, setTimestamp] = useState(() => {
    return initialTimestamp || Date.now();
  });

    // Store the reference 'now' time when component mounts
    const [now] = useState(() => Date.now());

    // Helper to format the difference as a human readable string
    const formatFromNow = (ts) => {
      let diff = ts - now;
      const sign = diff < 0 ? '-' : '';
      diff = Math.abs(diff);
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      let parts = [];
      if (hours) parts.push(`${hours}h`);
      if (minutes) parts.push(`${minutes}m`);
      if (seconds || parts.length === 0) parts.push(`${seconds}s`);
      return `${sign}${parts.join(' ')} from now`;
    };

  // Update parent component when timestamp changes
  useEffect(() => {
    if (onTimeChange) {
      onTimeChange(timestamp);
    }
  }, [timestamp, onTimeChange]);

  const adjustTime = (milliseconds) => {
    setTimestamp(prev => prev + milliseconds);
  };

  const formatDate = (ts) => {
    const date = new Date(ts);
    return date.toLocaleDateString('en-GB', {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTime = (ts) => {
    const date = new Date(ts);
    return date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const buttonStyle = {
    padding: '8px 12px',
    border: 'none',
    borderRadius: '6px',
    backgroundColor: '#f8f9fa',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'background-color 0.2s',
    minWidth: '60px'
  };

  const buttonHoverStyle = {
    backgroundColor: '#e9ecef'
  };

  return (
    <div style={{
      background: '#fff',
      padding: '24px',
      borderRadius: '12px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
      minWidth: '300px',
      maxWidth: '400px',
      textAlign: 'center'
    }}>
      {/* Close button if onClose provided */}
      {onClose && (
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            background: 'rgba(0,0,0,0.1)',
            border: 'none',
            borderRadius: '50%',
            width: '24px',
            height: '24px',
            cursor: 'pointer',
            fontSize: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          ×
        </button>
      )}

      {/* Date Display */}
      <div style={{
        fontSize: '18px',
        fontWeight: '600',
        color: '#333',
        marginBottom: '8px'
      }}>
        {formatDate(timestamp)}
      </div>

      {/* Time Display */}
      <div style={{
        fontSize: '24px',
        fontWeight: '700',
        color: '#36a2eb',
        marginBottom: '24px',
        fontFamily: 'monospace'
      }}>
        {formatTime(timestamp)}
      </div>

        {/* From now caption */}
        <div style={{
          fontSize: '13px',
          color: '#888',
          marginBottom: '16px',
          fontStyle: 'italic'
        }}>
          {formatFromNow(timestamp)}
        </div>

      {/* Hour Adjustment Buttons */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        gap: '8px',
        marginBottom: '12px'
      }}>
        <button
          style={buttonStyle}
          onMouseOver={(e) => e.target.style.backgroundColor = buttonHoverStyle.backgroundColor}
          onMouseOut={(e) => e.target.style.backgroundColor = buttonStyle.backgroundColor}
          onClick={() => adjustTime(-6 * 60 * 60 * 1000)} // -6 hours
        >
          -6h
        </button>
        <button
          style={buttonStyle}
          onMouseOver={(e) => e.target.style.backgroundColor = buttonHoverStyle.backgroundColor}
          onMouseOut={(e) => e.target.style.backgroundColor = buttonStyle.backgroundColor}
          onClick={() => adjustTime(-1 * 60 * 60 * 1000)} // -1 hour
        >
          -1h
        </button>
        <button
          style={buttonStyle}
          onMouseOver={(e) => e.target.style.backgroundColor = buttonHoverStyle.backgroundColor}
          onMouseOut={(e) => e.target.style.backgroundColor = buttonStyle.backgroundColor}
          onClick={() => adjustTime(1 * 60 * 60 * 1000)} // +1 hour
        >
          +1h
        </button>
        <button
          style={buttonStyle}
          onMouseOver={(e) => e.target.style.backgroundColor = buttonHoverStyle.backgroundColor}
          onMouseOut={(e) => e.target.style.backgroundColor = buttonStyle.backgroundColor}
          onClick={() => adjustTime(6 * 60 * 60 * 1000)} // +6 hours
        >
          +6h
        </button>
      </div>

      {/* Minute Adjustment Buttons */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        gap: '8px',
        marginBottom: '16px'
      }}>
        <button
          style={buttonStyle}
          onMouseOver={(e) => e.target.style.backgroundColor = buttonHoverStyle.backgroundColor}
          onMouseOut={(e) => e.target.style.backgroundColor = buttonStyle.backgroundColor}
          onClick={() => adjustTime(-15 * 60 * 1000)} // -15 minutes
        >
          -15m
        </button>
        <button
          style={buttonStyle}
          onMouseOver={(e) => e.target.style.backgroundColor = buttonHoverStyle.backgroundColor}
          onMouseOut={(e) => e.target.style.backgroundColor = buttonStyle.backgroundColor}
          onClick={() => adjustTime(-1 * 60 * 1000)} // -1 minute
        >
          -1m
        </button>
        <button
          style={buttonStyle}
          onMouseOver={(e) => e.target.style.backgroundColor = buttonHoverStyle.backgroundColor}
          onMouseOut={(e) => e.target.style.backgroundColor = buttonStyle.backgroundColor}
          onClick={() => adjustTime(1 * 60 * 1000)} // +1 minute
        >
          +1m
        </button>
        <button
          style={buttonStyle}
          onMouseOver={(e) => e.target.style.backgroundColor = buttonHoverStyle.backgroundColor}
          onMouseOut={(e) => e.target.style.backgroundColor = buttonStyle.backgroundColor}
          onClick={() => adjustTime(15 * 60 * 1000)} // +15 minutes
        >
          +15m
        </button>
      </div>


    </div>
  );
}